import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Niveles máximos de estudios usados por SEP 911 (Sección V)
const NIVELES_ESTUDIO = [
  ["primaria_incompleta", "Primaria incompleta"],
  ["primaria_terminada", "Primaria terminada"],
  ["secundaria_incompleta", "Secundaria incompleta"],
  ["secundaria_terminada", "Secundaria terminada"],
  ["profesional_tecnico", "Profesional técnico"],
  ["bachillerato_incompleto", "Bachillerato incompleto"],
  ["bachillerato_terminado", "Bachillerato terminado"],
  ["normal_preescolar_incompleta", "Normal preescolar incompleta"],
  ["normal_preescolar_terminada", "Normal preescolar terminada"],
  ["normal_primaria_incompleta", "Normal primaria incompleta"],
  ["normal_primaria_terminada", "Normal primaria terminada"],
  ["normal_superior_incompleta", "Normal superior incompleta"],
  ["normal_superior_pasante", "Normal superior pasante"],
  ["normal_superior_titulado", "Normal superior titulado"],
  ["licenciatura_incompleta", "Licenciatura incompleta"],
  ["licenciatura_pasante", "Licenciatura pasante"],
  ["licenciatura_titulado", "Licenciatura titulado"],
  ["maestria_incompleta", "Maestría incompleta"],
  ["maestria_graduado", "Maestría graduado"],
  ["doctorado_incompleto", "Doctorado incompleto"],
  ["doctorado_graduado", "Doctorado graduado"],
  ["otros", "Otros"],
];

const PERSONAL_ROLES_LIST = [
  ["directivo_con_grupo", "Directivo con grupo"],
  ["directivo_sin_grupo", "Directivo sin grupo"],
  ["subdirector_gestion", "Subdirector gestión"],
  ["subdirector_academico", "Subdirector académico"],
  ["docente", "Docente"],
  ["docente_educ_fisica", "Prof. educ. física"],
  ["docente_artes", "Prof. artísticas"],
  ["docente_idiomas", "Prof. idiomas"],
  ["administrativo", "Administrativo"],
];

const PERSONAL_ADMIN_DETALLE = [
  ["intendente", "Intendente"],
  ["conserje_velador", "Conserje / velador"],
  ["cocinera", "Cocinera"],
  ["aux_cocina", "Auxiliar de cocina"],
  ["acompanante_musica", "Acompañante de música"],
  ["asistente_educativo", "Asistente educativo"],
  ["otros", "Otros"],
];

const PERSONAL_ADMIN_PRI = [
  ["maestro_taller_lectura", "Maestro taller de lectura y escritura"],
  ["promotor_tic", "Promotor de TIC"],
  ["intendente", "Intendente"],
  ["conserje", "Conserje"],
  ["cocinera", "Cocinera"],
  ["aux_cocina", "Auxiliar de cocina"],
  ["velador", "Velador"],
  ["apoyo_admin", "Apoyo administrativo"],
  ["administrador", "Administrador"],
  ["prefecto", "Prefecto"],
  ["prefecto_nocturno", "Prefecto nocturno"],
  ["psicologo", "Psicólogo"],
  ["secretaria", "Secretaria"],
  ["medico", "Médico"],
  ["dentista", "Dentista"],
  ["trabajadora_social", "Trabajadora social"],
  ["contralor", "Responsable fondos / contralor"],
  ["almacenista", "Almacenista"],
  ["enfermera", "Enfermera"],
  ["jefe_oficina", "Jefe de oficina"],
  ["analista_admin", "Analista administrativo"],
  ["terapista", "Terapista"],
  ["especialista_tecnico", "Especialista técnico"],
  ["jefe_servicios", "Jefe de servicios y mantenimiento"],
  ["otros", "Otros"],
];

function gradosDe(nivel) {
  if (nivel === "PRIMARIA") return [["1","1°"],["2","2°"],["3","3°"],["4","4°"],["5","5°"],["6","6°"],["mas_un_grado","Más de un grado"]];
  if (nivel === "SECUNDARIA") return [["1","1°"],["2","2°"],["3","3°"],["mas_un_grado","Más de un grado"]];
  return [["1","1°"],["2","2°"],["3","3°"],["mas_un_grado","Más de un grado"]];
}
function adminDe(nivel) {
  return nivel === "PRIMARIA" ? PERSONAL_ADMIN_PRI : PERSONAL_ADMIN_DETALLE;
}

const GRADOS_PRE = [
  ["1", "1°"],
  ["2", "2°"],
  ["3", "3°"],
  ["mas_un_grado", "Más de un grado"],
];

function makeDefault() {
  return {
    existencia_fin_ciclo_pasado: Object.fromEntries(
      GRADOS_PRE.map(([k]) => [k, {
        H: 0, M: 0, grupos: 0, lengua_indigena: 0, nacido_fuera_mexico: 0,
        afro: 0, discapacidad: 0, trastorno: 0, aptitudes: 0, otras: 0,
      }])
    ),
    usaer: { H: 0, M: 0 },
    servicio_asistencial: { H: 0, M: 0, grupos: 0 },
    personal_por_nivel_estudios: Object.fromEntries(
      NIVELES_ESTUDIO.map(([k]) => [k, Object.fromEntries(PERSONAL_ROLES_LIST.map(([r]) => [r, { H: 0, M: 0 }]))])
    ),
    otros_estudios_especifique: "",
    personal_admin_detalle: Object.fromEntries(
      PERSONAL_ADMIN_DETALLE.map(([k]) => [k, { H: 0, M: 0 }])
    ),
    personal_admin_otros_especifique: "",
    docentes_con_grupo_por_grado: { "1": 0, "2": 0, "3": 0, "mas_un_grado": 0 },
    horas_semanales: { educ_fisica: 0, artisticas: 0, idiomas: 0 },
    aulas: {
      existentes: { "1": 0, "2": 0, "3": 0, "mas_un_grado": 0 },
      en_uso: { "1": 0, "2": 0, "3": 0, "mas_un_grado": 0 },
      adaptadas: { "1": 0, "2": 0, "3": 0, "mas_un_grado": 0 },
    },
    docentes_con_discapacidad: { H: 0, M: 0 },
    idiomas: {
      ingles: false, frances: false, aleman: false, japones: false,
      italiano: false, otro: false, otro_especifique: "",
    },
    libros_texto: { completos_inicio: true, semana_completos: "primera" },
    observaciones: "",
    fecha_llenado: "",
  };
}

function merge(defaults, saved) {
  if (saved === undefined || saved === null) return defaults;
  if (typeof defaults !== "object" || Array.isArray(defaults) || defaults === null) return saved;
  if (typeof saved !== "object" || Array.isArray(saved)) return saved;
  const out = {};
  const keys = new Set([...Object.keys(defaults), ...Object.keys(saved)]);
  for (const k of keys) {
    if (!(k in saved)) { out[k] = defaults[k]; continue; }
    if (!(k in defaults)) { out[k] = saved[k]; continue; }
    out[k] = merge(defaults[k], saved[k]);
  }
  return out;
}

export function Config911Dialog({ plantel, open, onOpenChange, onSaved, ciclo = "2026-2027" }) {
  const [values, setValues] = useState(makeDefault());
  const [saving, setSaving] = useState(false);
  const [auto911, setAuto911] = useState({});
  const nivel = plantel?.nivel || "PREESCOLAR";
  const grados = gradosDe(nivel);
  const adminList = adminDe(nivel);
  const isPri = nivel === "PRIMARIA";
  const isSec = nivel === "SECUNDARIA";
  const isPriOrSec = isPri || isSec;
  const gradosNum = isPri ? ["1","2","3","4","5","6"] : ["1","2","3"];

  useEffect(() => {
    if (plantel && open) {
      setValues(merge(makeDefault(), plantel.config_911));
      if (plantel.nivel === "SECUNDARIA") {
        api.get(`/planteles/${plantel.id}/auto-911?ciclo=${encodeURIComponent(ciclo)}`)
          .then((res) => setAuto911(res.data?.auto_911 || {}))
          .catch(() => setAuto911({}));
      } else {
        setAuto911({});
      }
    }
  }, [plantel, open, ciclo]);

  const upd = (path, value) => {
    setValues((prev) => {
      const next = structuredClone(prev);
      let cur = next;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (cur[key] === undefined || cur[key] === null || typeof cur[key] !== "object") {
          cur[key] = {};
        }
        cur = cur[key];
      }
      cur[path[path.length - 1]] = value;
      return next;
    });
  };

  const N = (path, testid) => (
    <Input
      type="number" min="0"
      data-testid={testid}
      value={path.reduce((o, k) => o?.[k], values) ?? 0}
      onChange={(e) => {
        const n = parseInt(e.target.value, 10);
        upd(path, Number.isFinite(n) && n >= 0 ? n : 0);
      }}
      className="h-8 text-sm"
    />
  );

  // Number input + "Auto: N (Usar)" hint for Secundaria auto-generated fields.
  const NAuto = (path, testid, autoPath) => {
    const autoVal = (autoPath || path).reduce((o, k) => o?.[k], auto911);
    const auto = Number(autoVal) || 0;
    return (
      <div className="space-y-0.5">
        {N(path, testid)}
        {isSec && auto > 0 && (
          <div className="flex items-center gap-1 text-[9px] leading-tight">
            <span className="px-1 rounded bg-[#BC955C]/15 text-[#7C5A1F] border border-[#BC955C]/30">
              Auto: {auto}
            </span>
            <button
              type="button"
              data-testid={`${testid}-usar-auto`}
              onClick={() => upd(path, auto)}
              className="text-[#235B4E] hover:underline"
            >
              Usar
            </button>
          </div>
        )}
      </div>
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/planteles/${plantel.id}/config-911`, values);
      toast.success("Configuración 911 guardada");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Configuración 911 · {plantel?.nombre}</DialogTitle>
          <p className="text-xs text-[#545454]">
            Campos manuales requeridos por el formato oficial y que no se derivan del alumnado.
          </p>
        </DialogHeader>
        <Tabs defaultValue="existencia" className="px-6">
          <TabsList className="grid grid-cols-4 md:grid-cols-10 gap-1 mb-3 h-auto">
            <TabsTrigger value="existencia" data-testid="tab-existencia">I. Existencia</TabsTrigger>
            {isPriOrSec && <TabsTrigger value="promovidos" data-testid="tab-promovidos">I. Promov.</TabsTrigger>}
            <TabsTrigger value="alumnado" data-testid="tab-alumnado">IV. Alumnado</TabsTrigger>
            {isPriOrSec && <TabsTrigger value="ingreso" data-testid="tab-ingreso">IV. Ingreso</TabsTrigger>}
            {isSec && <TabsTrigger value="egresados" data-testid="tab-egresados">IV. Egres./Rep.</TabsTrigger>}
            <TabsTrigger value="personal" data-testid="tab-personal">V. Personal</TabsTrigger>
            <TabsTrigger value="admin" data-testid="tab-admin">V. Admin</TabsTrigger>
            <TabsTrigger value="aulas" data-testid="tab-aulas">VI. Aulas</TabsTrigger>
            <TabsTrigger value="idiomas" data-testid="tab-idiomas">VII-VIII</TabsTrigger>
            <TabsTrigger value="oficial" data-testid="tab-oficial">Firma</TabsTrigger>
          </TabsList>

          <TabsContent value="existencia" className="pb-4">
            <SectionTitle>I. Existencia — Fin del ciclo escolar anterior</SectionTitle>
            <div className="border border-[#E5E7EB] rounded overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F9FAFB] text-[10px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="text-left px-2 py-1.5">Grado</th>
                    <th className="px-2">H</th><th className="px-2">M</th>
                    <th className="px-2">Grupos</th>
                    <th className="px-2">L. indígena</th>
                    <th className="px-2">Fuera MX</th>
                    <th className="px-2">Afro</th>
                    <th className="px-2">Disc.</th>
                    <th className="px-2">Trast.</th>
                    <th className="px-2">Apt.</th>
                    <th className="px-2">Otras</th>
                  </tr>
                </thead>
                <tbody>
                  {grados.map(([k, label]) => (
                    <tr key={k} className="border-t border-[#E5E7EB]">
                      <td className="px-2 py-1 font-medium">{label}</td>
                      {["H", "M", "grupos", "lengua_indigena", "nacido_fuera_mexico", "afro", "discapacidad", "trastorno", "aptitudes", "otras"].map((f) => (
                        <td key={f} className="p-1 w-16">
                          {N(["existencia_fin_ciclo_pasado", k, f], `exist-${k}-${f}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {isPriOrSec && (
            <TabsContent value="promovidos" className="pb-4 space-y-4">
              <SectionTitle>I.2 Alumnado promovido (ciclo anterior)</SectionTitle>
              <div className="border border-[#E5E7EB] rounded overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr>
                      <th className="text-left px-2 py-1.5">Grado</th>
                      <th className="px-2">H</th><th className="px-2">M</th>
                      <th className="px-2">L. indígena</th>
                      <th className="px-2">Fuera MX</th>
                      <th className="px-2">Afro</th>
                      <th className="px-2">Disc.</th>
                      <th className="px-2">Trast.</th>
                      <th className="px-2">Apt.</th>
                      <th className="px-2">Otras</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradosNum.map((k) => (
                      <tr key={k} className="border-t border-[#E5E7EB]">
                        <td className="px-2 py-1 font-medium">{k}°</td>
                        {["H","M","lengua_indigena","nacido_fuera_mexico","afro","discapacidad","trastorno","aptitudes","otras"].map((f) => (
                          <td key={f} className="p-1 w-16">
                            {N(["promovidos_por_grado", k, f], `prom-${k}-${f}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {isPri && (
                <>
                  <SectionTitle>I.3 Promovidos de 6° por edad</SectionTitle>
                  <div className="border border-[#E5E7EB] rounded overflow-hidden max-w-3xl">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                        <tr>
                          <th className="text-left px-2 py-1.5">Sexo</th>
                          {["10","11","12","13","14","15"].map((e) => (
                            <th key={e} className="px-2 py-1">{e === "15" ? "15+" : e} años</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {["H","M"].map((gen) => (
                          <tr key={gen} className="border-t border-[#E5E7EB]">
                            <td className="px-2 py-1 font-medium">{gen === "H" ? "Hombres" : "Mujeres"}</td>
                            {["10","11","12","13","14","15"].map((e) => (
                              <td key={e} className="p-1 w-20">
                                {N(["promovidos_6to_por_edad", e, gen], `prom6-${e}-${gen}`)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>
          )}

          <TabsContent value="alumnado" className="pb-4 space-y-4">
            <SectionTitle>IV.3 Servicio asistencial</SectionTitle>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              <Field label="Hombres">{N(["servicio_asistencial", "H"], "serv-H")}</Field>
              <Field label="Mujeres">{N(["servicio_asistencial", "M"], "serv-M")}</Field>
              <Field label="Grupos">{N(["servicio_asistencial", "grupos"], "serv-grupos")}</Field>
            </div>
            <SectionTitle>IV.6 USAER — Alumnado atendido</SectionTitle>
            <div className="grid grid-cols-2 gap-3 max-w-sm">
              <Field label="Hombres">{NAuto(["usaer", "H"], "usaer-H")}</Field>
              <Field label="Mujeres">{NAuto(["usaer", "M"], "usaer-M")}</Field>
            </div>
          </TabsContent>

          <TabsContent value="personal" className="pb-4">
            <SectionTitle>V.1 Personal por nivel de estudios y función</SectionTitle>
            <div className="border border-[#E5E7EB] rounded overflow-auto max-h-[60vh]">
              <table className="text-[10px] min-w-full">
                <thead className="bg-[#F9FAFB] sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-2 py-1.5 min-w-[160px]">Nivel de estudios</th>
                    {PERSONAL_ROLES_LIST.map(([k, label]) => (
                      <th key={k} colSpan={2} className="px-1 border-l border-[#E5E7EB]" title={label}>
                        <div className="truncate max-w-[110px]">{label}</div>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th></th>
                    {PERSONAL_ROLES_LIST.map(([k]) => (
                      <>
                        <th key={`${k}-h`} className="px-1 text-[#235B4E] border-l border-[#E5E7EB]">H</th>
                        <th key={`${k}-m`} className="px-1 text-[#9F2241]">M</th>
                      </>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {NIVELES_ESTUDIO.map(([nivel, label]) => (
                    <tr key={nivel} className="border-t border-[#E5E7EB]">
                      <td className="px-2 py-1">{label}</td>
                      {PERSONAL_ROLES_LIST.map(([role]) => (
                        <>
                          <td key={`${nivel}-${role}-h`} className="p-0.5 border-l border-[#E5E7EB] w-12">
                            {N(["personal_por_nivel_estudios", nivel, role, "H"], `pne-${nivel}-${role}-H`)}
                          </td>
                          <td key={`${nivel}-${role}-m`} className="p-0.5 w-12">
                            {N(["personal_por_nivel_estudios", nivel, role, "M"], `pne-${nivel}-${role}-M`)}
                          </td>
                        </>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 max-w-md">
              <Label className="text-xs">Especifique "Otros" (nivel de estudios)</Label>
              <Input
                value={values.otros_estudios_especifique}
                onChange={(e) => upd(["otros_estudios_especifique"], e.target.value)}
              />
            </div>
          </TabsContent>

          {isPriOrSec && (
            <TabsContent value="ingreso" className="pb-4 space-y-4">
              <SectionTitle>IV.1 Nuevo ingreso y repetidores por grado</SectionTitle>
              <div className="border border-[#E5E7EB] rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr>
                      <th className="text-left px-2 py-1.5" rowSpan={2}>Grado</th>
                      <th colSpan={2} className="px-2 border-l border-[#E5E7EB]">Nuevo ingreso</th>
                      <th colSpan={2} className="px-2 border-l border-[#E5E7EB]">Repetidores</th>
                    </tr>
                    <tr>
                      <th className="px-2 border-l border-[#E5E7EB]">H</th><th className="px-2">M</th>
                      <th className="px-2 border-l border-[#E5E7EB]">H</th><th className="px-2">M</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradosNum.map((k) => (
                      <tr key={k} className="border-t border-[#E5E7EB]">
                        <td className="px-2 py-1 font-medium">{k}°</td>
                        {["nuevo_ingreso","repetidores"].map((cat) => (
                          <>
                            <td key={`${cat}-H`} className="p-1 w-16 border-l border-[#E5E7EB]">
                              {isSec
                                ? NAuto(["ingreso_repetidores", k, cat, "H"], `ir-${k}-${cat}-H`)
                                : N(["ingreso_repetidores", k, cat, "H"], `ir-${k}-${cat}-H`)}
                            </td>
                            <td key={`${cat}-M`} className="p-1 w-16">
                              {isSec
                                ? NAuto(["ingreso_repetidores", k, cat, "M"], `ir-${k}-${cat}-M`)
                                : N(["ingreso_repetidores", k, cat, "M"], `ir-${k}-${cat}-M`)}
                            </td>
                          </>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {isPri && (
                <>
                  <SectionTitle>IV.3 Alumnos de 1° según preescolar cursado</SectionTitle>
                  <div className="border border-[#E5E7EB] rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                        <tr>
                          <th className="text-left px-2 py-1.5" rowSpan={2}>Años preescolar</th>
                          <th colSpan={3} className="px-2 border-l">Nuevo ingreso</th>
                          <th colSpan={3} className="px-2 border-l">Repetidores</th>
                        </tr>
                        <tr>
                          <th className="px-1 border-l">H</th><th className="px-1">M</th><th className="px-1">c/Disc.</th>
                          <th className="px-1 border-l">H</th><th className="px-1">M</th><th className="px-1">c/Disc.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[["1_ano","1 año"],["2_anos","2 años"],["3_anos","3 años"]].map(([k, label]) => (
                          <tr key={k} className="border-t border-[#E5E7EB]">
                            <td className="px-2 py-1 font-medium">{label}</td>
                            {["nuevo_ingreso","repetidores"].map((cat) => (
                              <>
                                {["H","M","con_discapacidad"].map((f) => (
                                  <td key={`${cat}-${f}`} className={`p-1 w-14 ${f === "H" ? "border-l border-[#E5E7EB]" : ""}`}>
                                    {N(["primer_grado_preescolar", k, cat, f], `pg-${k}-${cat}-${f}`)}
                                  </td>
                                ))}
                              </>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <SectionTitle>{isSec ? "IV.3 Origen escuela primaria por grado" : "IV.7 Origen preescolar por grado"}</SectionTitle>
              <div className="border border-[#E5E7EB] rounded overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr>
                      <th className="text-left px-2 py-1.5" rowSpan={2}>Tipo</th>
                      {gradosNum.map((k) => (
                        <th key={k} colSpan={2} className="px-1 border-l">{k}°</th>
                      ))}
                    </tr>
                    <tr>
                      {gradosNum.map((k) => (
                        <>
                          <th key={`${k}-H`} className="px-1 border-l">H</th>
                          <th key={`${k}-M`} className="px-1">M</th>
                        </>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[["general", isSec ? "Prim. General" : "P. General"],["indigena", isSec ? "Prim. Indígena" : "P. Indígena"],["comunitaria", isSec ? "Prim. Comunitaria" : "P. Comunitaria"]].map(([tipo, label]) => (
                      <tr key={tipo} className="border-t border-[#E5E7EB]">
                        <td className="px-2 py-1 font-medium">{label}</td>
                        {gradosNum.map((k) => (
                          <>
                            <td key={`${k}-H`} className="p-1 w-14 border-l border-[#E5E7EB]">
                              {N(["origen_preescolar", tipo, k, "H"], `op-${tipo}-${k}-H`)}
                            </td>
                            <td key={`${k}-M`} className="p-1 w-14">
                              {N(["origen_preescolar", tipo, k, "M"], `op-${tipo}-${k}-M`)}
                            </td>
                          </>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionTitle>{isSec ? "IV.4 Lenguas maternas (primaria indígena)" : "IV.8 Lenguas maternas (preescolar indígena)"}</SectionTitle>
              <div className="space-y-1.5 max-w-xl">
                <Label className="text-xs">Lenguas registradas (separadas por coma, formato "clave:lengua")</Label>
                <Input
                  data-testid="lenguas-maternas"
                  placeholder="Ej: NAH:Náhuatl, MAY:Maya, TZE:Tzeltal"
                  value={values.lenguas_maternas_texto || ""}
                  onChange={(e) => upd(["lenguas_maternas_texto"], e.target.value)}
                />
              </div>
            </TabsContent>
          )}

          {isSec && (
            <TabsContent value="egresados" className="pb-4 space-y-4">
              <SectionTitle>IV.8 Egresados de 3° durante el ciclo anterior</SectionTitle>
              <div className="border border-[#E5E7EB] rounded overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr>
                      <th className="text-left px-2 py-1.5">Sexo</th>
                      {["14","15","16","17","18"].map((e) => (
                        <th key={e} className="px-2 py-1">{e === "18" ? "18+" : e} años</th>
                      ))}
                      <th className="px-2 py-1">L. indígena</th>
                      <th className="px-2 py-1">Fuera MX</th>
                      <th className="px-2 py-1">Disc.</th>
                      <th className="px-2 py-1">Trast.</th>
                      <th className="px-2 py-1">Otras</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["H","M"].map((gen) => (
                      <tr key={gen} className="border-t border-[#E5E7EB]">
                        <td className="px-2 py-1 font-medium">{gen === "H" ? "Hombres" : "Mujeres"}</td>
                        {["14","15","16","17","18"].map((e) => (
                          <td key={e} className="p-1 w-16">
                            {N(["egresados_3ro_por_edad", e, gen], `egr-${e}-${gen}`)}
                          </td>
                        ))}
                        {["lengua_indigena","nacido_fuera_mexico","discapacidad","trastorno","otras"].map((f) => (
                          <td key={f} className="p-1 w-16">
                            {N(["egresados_3ro_extra", gen, f], `egr-${gen}-${f}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionTitle>IV.9 Alumnos que reprobaron una o más asignaturas</SectionTitle>
              <div className="border border-[#E5E7EB] rounded overflow-hidden max-w-xl">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr>
                      <th className="text-left px-2 py-1.5">Grado</th>
                      <th className="px-2">H</th><th className="px-2">M</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["1","2","3"].map((k) => (
                      <tr key={k} className="border-t border-[#E5E7EB]">
                        <td className="px-2 py-1 font-medium">{k}°</td>
                        <td className="p-1 w-20">{NAuto(["reprobaron_asignaturas", k, "H"], `repr-${k}-H`)}</td>
                        <td className="p-1 w-20">{NAuto(["reprobaron_asignaturas", k, "M"], `repr-${k}-M`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionTitle>IV.10 Alumnos regularizados (aprobaron todas las asignaturas)</SectionTitle>
              <div className="border border-[#E5E7EB] rounded overflow-hidden max-w-xl">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr>
                      <th className="text-left px-2 py-1.5">Grado</th>
                      <th className="px-2">H</th><th className="px-2">M</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["1","2","3"].map((k) => (
                      <tr key={k} className="border-t border-[#E5E7EB]">
                        <td className="px-2 py-1 font-medium">{k}°</td>
                        <td className="p-1 w-20">{NAuto(["regularizados", k, "H"], `reg-${k}-H`)}</td>
                        <td className="p-1 w-20">{NAuto(["regularizados", k, "M"], `reg-${k}-M`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionTitle>IV.11 Alumnos irregulares (continúan)</SectionTitle>
              <div className="border border-[#E5E7EB] rounded overflow-hidden max-w-xl">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                    <tr>
                      <th className="text-left px-2 py-1.5">Grado</th>
                      <th className="px-2">H</th><th className="px-2">M</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["2","3"].map((k) => (
                      <tr key={k} className="border-t border-[#E5E7EB]">
                        <td className="px-2 py-1 font-medium">{k}°</td>
                        <td className="p-1 w-20">{NAuto(["irregulares", k, "H"], `irr-${k}-H`)}</td>
                        <td className="p-1 w-20">{NAuto(["irregulares", k, "M"], `irr-${k}-M`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionTitle>IV.12 Directivos con grupo y docentes — SOLO TELESECUNDARIAS</SectionTitle>
              <label className="flex items-center gap-2 max-w-md">
                <Checkbox
                  data-testid="es-telesecundaria"
                  checked={!!values.es_telesecundaria}
                  onCheckedChange={(v) => upd(["es_telesecundaria"], !!v)}
                />
                <span className="text-sm">Este plantel es Telesecundaria</span>
              </label>
              {values.es_telesecundaria && (
                <div className="border border-[#E5E7EB] rounded overflow-hidden max-w-2xl">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F9FAFB] text-[10px] uppercase text-[#6B7280]">
                      <tr>
                        <th className="text-left px-2 py-1.5">Grado</th>
                        <th className="px-2">Directivos con grupo</th>
                        <th className="px-2">Docentes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {["1","2","3","mas_un_grado"].map((k) => (
                        <tr key={k} className="border-t border-[#E5E7EB]">
                          <td className="px-2 py-1 font-medium">{k === "mas_un_grado" ? "Más de un grado" : `${k}°`}</td>
                          <td className="p-1 w-32">{N(["telesecundaria_staff", k, "directivos"], `tele-${k}-dir`)}</td>
                          <td className="p-1 w-32">{N(["telesecundaria_staff", k, "docentes"], `tele-${k}-doc`)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          )}

          <TabsContent value="admin" className="pb-4 space-y-4">
            <SectionTitle>V.2 Personal administrativo, auxiliar y de servicios</SectionTitle>
            <div className="border border-[#E5E7EB] rounded overflow-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-xs uppercase tracking-wider text-[#6B7280] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Función</th>
                    <th className="px-3 py-2 w-24">Hombres</th>
                    <th className="px-3 py-2 w-24">Mujeres</th>
                    <th className="px-3 py-2 w-20">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {adminList.map(([k, label]) => {
                    const v = values.personal_admin_detalle[k] || { H: 0, M: 0 };
                    return (
                      <tr key={k} className="border-t border-[#E5E7EB]">
                        <td className="px-3 py-1.5">{label}</td>
                        <td className="px-2">{N(["personal_admin_detalle", k, "H"], `admin-${k}-H`)}</td>
                        <td className="px-2">{N(["personal_admin_detalle", k, "M"], `admin-${k}-M`)}</td>
                        <td className="px-3 text-center font-semibold">{(v.H || 0) + (v.M || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="max-w-md">
              <Label className="text-xs">Especifique "Otros" (administrativo)</Label>
              <Input
                value={values.personal_admin_otros_especifique}
                onChange={(e) => upd(["personal_admin_otros_especifique"], e.target.value)}
              />
            </div>
          </TabsContent>

          <TabsContent value="grados" className="pb-4 space-y-4">
            <SectionTitle>V.3 Docentes con grupo (más directivo con grupo) por grado</SectionTitle>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-3 max-w-4xl">
              {grados.map(([k, label]) => (
                <Field key={k} label={label}>{N(["docentes_con_grupo_por_grado", k], `docgr-${k}`)}</Field>
              ))}
            </div>
            <SectionTitle>V.4 Horas semanales — personal docente especial</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
              <Field label="Educación física">{N(["horas_semanales", "educ_fisica"], "hrs-ef")}</Field>
              <Field label="Artísticas / música / danza">{N(["horas_semanales", "artisticas"], "hrs-art")}</Field>
              {isPri && (
                <Field label="Actividades tecnológicas">{N(["horas_semanales", "tecnologicas"], "hrs-tec")}</Field>
              )}
              <Field label="Idiomas">{N(["horas_semanales", "idiomas"], "hrs-idi")}</Field>
            </div>
            <SectionTitle>VI.5 Docentes con discapacidad</SectionTitle>
            <div className="grid grid-cols-2 gap-3 max-w-sm">
              <Field label="Hombres">{N(["docentes_con_discapacidad", "H"], "docdisc-H")}</Field>
              <Field label="Mujeres">{N(["docentes_con_discapacidad", "M"], "docdisc-M")}</Field>
            </div>
          </TabsContent>

          <TabsContent value="aulas" className="pb-4">
            <SectionTitle>VI. Aulas</SectionTitle>
            <div className="border border-[#E5E7EB] rounded overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-xs uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="text-left px-3 py-2">Aulas</th>
                    {grados.map(([k, label]) => (
                      <th key={k} className="px-3 py-2 w-20">{label}</th>
                    ))}
                    <th className="px-3 py-2 w-20">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {[["existentes", "Existentes"], ["en_uso", "En uso"], ["adaptadas", "Adaptadas"]].map(([tipo, label]) => {
                    const row = values.aulas[tipo] || {};
                    const tot = grados.reduce((s, [k]) => s + (row[k] || 0), 0);
                    return (
                      <tr key={tipo} className="border-t border-[#E5E7EB]">
                        <td className="px-3 py-1.5 font-medium">{label}</td>
                        {grados.map(([k]) => (
                          <td key={k} className="p-1">{N(["aulas", tipo, k], `aula-${tipo}-${k}`)}</td>
                        ))}
                        <td className="text-center font-semibold">{tot}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="idiomas" className="pb-4 space-y-4">
            <SectionTitle>VII. Idiomas impartidos</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-xl">
              {[
                ["ingles", "Inglés"], ["frances", "Francés"], ["aleman", "Alemán"],
                ["japones", "Japonés"], ["italiano", "Italiano"], ["otro", "Otro"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    data-testid={`idioma-${k}`}
                    checked={!!values.idiomas[k]}
                    onCheckedChange={(v) => upd(["idiomas", k], !!v)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
            {values.idiomas.otro && (
              <div className="max-w-md">
                <Label className="text-xs">Especifique otro idioma</Label>
                <Input
                  value={values.idiomas.otro_especifique}
                  onChange={(e) => upd(["idiomas", "otro_especifique"], e.target.value)}
                />
              </div>
            )}

            <SectionTitle>VIII. Libros de texto</SectionTitle>
            <div className="space-y-3 max-w-xl">
              <label className="flex items-center gap-2">
                <Checkbox
                  data-testid="libros-completos"
                  checked={values.libros_texto.completos_inicio}
                  onCheckedChange={(v) => upd(["libros_texto", "completos_inicio"], !!v)}
                />
                <span className="text-sm">
                  Al inicio del ciclo la escuela contaba con libros de texto completos
                </span>
              </label>
              {!values.libros_texto.completos_inicio && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Semana en que se completaron los libros</Label>
                  <Select
                    value={values.libros_texto.semana_completos}
                    onValueChange={(v) => upd(["libros_texto", "semana_completos"], v)}
                  >
                    <SelectTrigger data-testid="libros-semana"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="primera">Primera semana</SelectItem>
                      <SelectItem value="segunda">Segunda semana</SelectItem>
                      <SelectItem value="tercera">Tercera semana</SelectItem>
                      <SelectItem value="cuarta_o_mas">Cuarta semana o más</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="oficial" className="pb-4 space-y-4">
            <SectionTitle>Oficialización</SectionTitle>
            <div className="space-y-1.5">
              <Label className="text-xs">Observaciones (llenado, plataforma, etc.)</Label>
              <Textarea
                data-testid="observaciones"
                rows={5}
                value={values.observaciones}
                onChange={(e) => upd(["observaciones"], e.target.value)}
              />
            </div>
            <div className="space-y-1.5 max-w-xs">
              <Label className="text-xs">Fecha de llenado</Label>
              <Input
                type="date"
                data-testid="fecha-llenado"
                value={values.fecha_llenado}
                onChange={(e) => upd(["fecha_llenado"], e.target.value)}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="px-6 py-4 border-t border-[#E5E7EB]">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="btn-save-config911" onClick={save} disabled={saving} className="bg-[#235B4E] hover:bg-[#1D4A40]">
            {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mb-2 pb-1 border-b border-[#E5E7EB]">
      <div className="text-xs font-semibold uppercase tracking-wider text-[#235B4E]">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
